import type {
  AdminSubscriptionDetail,
  AdminSubscriptionRow,
  BillingPeriod,
  SubscriptionStatus,
  SubscriptionType,
  TriggerEvent,
} from './types';

export interface ListParams {
  type?: SubscriptionType[];
  status?: SubscriptionStatus[];
  search?: string;
  triggerFired?: 'yes' | 'no' | 'all';
  page?: number;
  pageSize?: number;
}

export async function fetchAdminSubscriptions(
  params: ListParams = {},
): Promise<{ rows: AdminSubscriptionRow[]; total: number; page: number; page_size: number }> {
  const q = new URLSearchParams();
  for (const t of params.type ?? []) q.append('type', t);
  for (const s of params.status ?? []) q.append('status', s);
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.triggerFired && params.triggerFired !== 'all') {
    q.set('trigger_fired', params.triggerFired);
  }
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('page_size', String(params.pageSize));

  const r = await fetch(`/api/admin/subscriptions?${q.toString()}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export async function fetchAdminSubscription(id: string): Promise<AdminSubscriptionDetail> {
  const r = await fetch(`/api/admin/subscriptions/${id}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export interface PatchSubInput {
  monthly_price?: string;
  billing_period?: BillingPeriod;
  contract_length_months?: number | null;
  monthly_hour_minimum?: string | null;
  basic_abo_contents?: string[];
  trigger_event?: TriggerEvent;
  setup_trigger_task_id?: string | null;
}

export async function patchAdminSubscription(
  id: string,
  patch: PatchSubInput,
): Promise<AdminSubscriptionDetail['subscription']> {
  const r = await fetch(`/api/admin/subscriptions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${r.status}`);
  }
  const data = (await r.json()) as { subscription: AdminSubscriptionDetail['subscription'] };
  return data.subscription;
}
